/**
 * AgentInviteBanner — appears on the athlete's own dashboard when an
 * agent has requested to attach them to their roster.  The athlete
 * MUST approve before the agent gets access — enforces consent flow
 * introduced in iter158.
 *
 * Renders one card per pending invite with a bright emerald "Approve
 * this is my agent" pill and a subtle "Decline" secondary.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, Check, X, ShieldCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import AgencyDetailsModal from "./AgencyDetailsModal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentInviteBanner() {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState({});
  const [openInviteId, setOpenInviteId] = useState("");

  const load = () => {
    axios
      .get(`${API}/api/me/athlete/agent-invites`, { withCredentials: true })
      .then((r) => setInvites(r.data?.rows || []))
      .catch(() => setInvites([]));
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (invite_id, action) => {
    setBusy((b) => ({ ...b, [invite_id]: action }));
    try {
      const r = await axios.post(
        `${API}/api/me/athlete/agent-invites/${action}`,
        { invite_id },
        { withCredentials: true },
      );
      if (action === "approve") toast.success(`${r.data.agent_name || "Agent"} is now your agent`);
      else toast.success("Invite declined");
      setInvites((prev) => prev.filter((i) => i.id !== invite_id));
    } catch (err) {
      // iter231 · Surface the real backend reason (e.g. 402
      // "agent_roster_capacity_reached") instead of a generic toast.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Could not update invite";
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy((b) => ({ ...b, [invite_id]: false }));
    }
  };

  if (!invites.length) return null;

  return (
    <>
    <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-6" data-testid="agent-invite-banner">
      {invites.map((inv) => (
        <button
          type="button"
          key={inv.id}
          onClick={() => setOpenInviteId(inv.id)}
          data-testid={`agent-invite-${inv.id}`}
          className="w-full text-left mb-3 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/[0.12] via-white/[0.02] to-white/[0.02] p-4 sm:p-5 hover:border-emerald-300/60 hover:bg-emerald-500/[0.16] transition group"
          style={{ boxShadow: "0 12px 32px -12px rgba(16,185,129,0.4)" }}
        >
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0" />
            <p className="text-[10px] uppercase tracking-[0.32em] font-bold text-emerald-300 flex-1">Agent request · pending your approval</p>
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white/40 group-hover:text-white/70 transition">
              See details <ChevronRight className="w-3 h-3" />
            </span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-amber-400 to-fuchsia-500 flex items-center justify-center text-lg font-bold shrink-0">
              {inv.agent_photo
                ? <img src={inv.agent_photo} alt="" className="w-full h-full object-cover" />
                : (inv.agency_name || "A").charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white flex items-center gap-1.5 truncate">
                {inv.agency_name}
                <BadgeCheck className="w-4 h-4 text-cyan-300 shrink-0" />
              </p>
              <p className="text-xs text-white/60 mt-0.5">wants to add you to their roster.  You control this — tap to see who they are.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto" onClick={(e) => e.stopPropagation()}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (inv.capacity_reached) {
                    toast.error(`${inv.agency_name || "Your agent"}'s roster is full (${inv.roster_count}${inv.roster_capacity ? `/${inv.roster_capacity}` : ""}). Ask them to upgrade their plan before you approve.`, { duration: 7000 });
                    return;
                  }
                  decide(inv.id, "approve");
                }}
                data-testid={`agent-invite-approve-${inv.id}`}
                aria-disabled={!!busy[inv.id] || !!inv.capacity_reached}
                className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full font-bold text-xs uppercase tracking-widest px-4 py-2.5 transition cursor-pointer select-none ${
                  inv.capacity_reached
                    ? "bg-white/10 text-white/50 border border-white/10"
                    : "bg-emerald-400 hover:bg-emerald-300 text-black"
                } ${busy[inv.id] ? "opacity-50 pointer-events-none" : ""}`}
                style={inv.capacity_reached ? undefined : { boxShadow: "0 0 0 1px rgba(16,185,129,0.4), 0 8px 20px -4px rgba(16,185,129,0.55)" }}
              >
                <Check className="w-3.5 h-3.5" />
                {inv.capacity_reached ? "Roster full · ask agent to upgrade" : "Approve · This is my agent"}
              </span>
              <span
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); decide(inv.id, "reject"); }}
                data-testid={`agent-invite-reject-${inv.id}`}
                aria-disabled={!!busy[inv.id]}
                className={`inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 text-xs uppercase tracking-widest font-bold px-4 py-2.5 transition cursor-pointer select-none ${busy[inv.id] ? "opacity-50 pointer-events-none" : ""}`}
              >
                <X className="w-3.5 h-3.5" />
                Decline
              </span>
            </div>
          </div>
          {inv.capacity_reached && (
            <p
              className="mt-3 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 leading-snug"
              data-testid={`agent-invite-capacity-warning-${inv.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ShieldCheck className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              Heads up: {inv.agency_name || "your agent"} is trying to add you but their roster is full ({inv.roster_count}{inv.roster_capacity ? ` / ${inv.roster_capacity}` : ""}). They&rsquo;ll need to upgrade their plan before you can accept — reach out and let them know.
            </p>
          )}
        </button>
      ))}
    </div>
    <AgencyDetailsModal
      open={!!openInviteId}
      inviteId={openInviteId}
      onClose={() => setOpenInviteId("")}
      onApproved={(id) => setInvites((prev) => prev.filter((i) => i.id !== id))}
      onDeclined={(id) => setInvites((prev) => prev.filter((i) => i.id !== id))}
    />
    </>
  );
}
